<template>
  <drag-resize type="customSort" :drag-box-style="dragBoxStyle" :drag-box-head-style="dragBoxHeadStyle">
    <template #header>
      <span>Custom Sort</span>
      <i class="icon-close closeIcon" @click="closeCustomSort"></i>
    </template>

    <template #body>
      <div class="custom-sort__wrapper">
        <div class="options">
          <div class="options__addMinus">
            <i class="icon-plus" @click="addSortOption"></i>
            <i class="icon-minus" @click="removeSortOption"></i>
            <i class="icon-copy"></i>
          </div>
          <div class="options--listWrapper">
            <div class="selected" @click="toggleList">
              <div>{{ selectedOption }}</div>
              <i class="selectedOption" :class="showList ? icons.MenuUp : icons.MenuDown"></i>
            </div>
            <div v-if="showList" v-outside-click="handleOutsideClick" class="options--list">
              <div class="options--headItem" @click="toggleList">
                Options <i :class="showList ? icons.MenuUp : icons.MenuDown"></i>
              </div>
              <div v-for="(option, i) of options" :key="i" class="options--item">
                <radio-box
                  v-if="option.type === 'radio'"
                  :item="option"
                  :is-checked="option.id === currentItemID"
                  @click="handleSelection(option.id)"
                />
                <div v-if="option.type == 'hr'" class="options--item__seperator"></div>
                <checkbox
                  v-if="option.type == 'checkbox'"
                  :item="option"
                  :is-checked="isSensitive"
                  @passItem="toggleSensitive"
                />
              </div>
            </div>
          </div>

          <div class="options--checkbox">
            <checkbox
              :item="{ label: 'My data has headers', value: 'addtomylist' }"
              :is-checked="isAdded"
              @passItem="handleAdd"
            />
          </div>
        </div>
        <div class="custom-sort__body">
          <div class="custom-sort__body--column">
            <div class="columnText columnItem">Column</div>
            <div class="columnItem">Sort By</div>
            <div class="columnItem">Order</div>
          </div>
          <div class="custom-sort__body--items">
            <div
              v-for="(option, index) in sortOptions"
              :key="index"
              :class="['custom-sort__body--item', { 'custom-sort__body--item--active': currentIndex === index }]"
              @mouseenter="currentIndex = index"
              @mouseleave="currentIndex = null"
            >
              <!-- Column Dropdown -->
              <div class="custom-sort__body--sub">
                <div class="selected">
                  <span>{{ option.columnValue }}</span>
                  <i
                    :class="option.columnOpen ? icons.MenuUp : icons.MenuDown"
                    @click="openDropdown(option, $event, 'columnOpen', 'dropdownPosition')"
                  ></i>
                </div>
                <select-option
                  v-if="option.columnOpen"
                  :options="columnValues"
                  :default="option.columnValue"
                  :style="option.dropdownPosition"
                  @input="option.columnValue = $event"
                  @close="closeDropdown(option, 'columnOpen')"
                />
              </div>

              <!-- Sort By Dropdown -->
              <div class="custom-sort__body--sub">
                <div class="selected">
                  <span>{{ option.sortby }}</span>
                  <i
                    :class="option.sortByOpen ? icons.MenuUp : icons.MenuDown"
                    @click="openDropdown(option, $event, 'sortByOpen', 'sortByPosition')"
                  ></i>
                </div>
                <select-option
                  v-if="option.sortByOpen"
                  :options="sortByValues"
                  :default="option.sortby"
                  :style="option.sortByPosition"
                  @input="option.sortby = $event"
                  @close="closeDropdown(option, 'sortByOpen')"
                />
              </div>

              <!-- Order By Dropdown -->
              <div class="custom-sort__body--sub">
                <div class="selected">
                  <span>{{ option.orderBy }}</span>
                  <i
                    :class="option.orderByOpen ? icons.MenuUp : icons.MenuDown"
                    @click="openDropdown(option, $event, 'orderByOpen', 'orderByPosition')"
                  ></i>
                </div>
                <select-option
                  v-if="option.orderByOpen"
                  :options="orderByValues"
                  :default="option.orderBy"
                  :style="option.orderByPosition"
                  @input="option.orderBy = $event"
                  @close="closeDropdown(option, 'orderByOpen')"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <template #footer>
      <div class="custom-sort__footer">
        <div class="custom-sort__actions">
          <custom-button
            button-type="button"
            button-class="btn btn-default"
            class="custom-sort__actions--cancel"
            @click="closeCustomSort"
          >
            {{ $t("actions.cancel") }}
          </custom-button>

          <custom-button
            class="custom-sort__actions--add"
            button-type="button"
            button-class="btn btn-default"
            @click="closeCustomSort"
          >
            {{ $t("actions.ok") }}
          </custom-button>
        </div>
      </div>
    </template>
  </drag-resize>
</template>

<script>
import SelectOption from "./SelectOption.vue";
import Checkbox from "./Checkbox.vue";
import RadioBox from "./RadioBox.vue";
import CustomButton from "./CustomButton.vue";
import { icons } from "../../utils/icons";
import hotkeys from "hotkeys-js";
import { HotkeyScopes } from "../../utils/constants";
import DragResize from "./DragResize.vue";

export default {
  components: {
    SelectOption,
    Checkbox,
    CustomButton,
    RadioBox,
    DragResize,
  },
  data() {
    return {
      sortOptions: [
        {
          columnValue: "Sort by",
          columnOpen: false,
          sortby: "Values",
          sortByOpen: false,
          orderBy: "A to Z",
          orderByOpen: false,
        },
      ],
      columnValues: ["Column 1", "Column 2", "Column 3", "Column 4"],
      sortByValues: ["Cell Color", "Font Color", "Cell Style", "Cell Icon"],
      orderByValues: ["A to Z", "Z to A"],
      currentIndex: -1,
      keyPressed: null,
      isSensitive: false,
      currentItemID: null,
      icons,
      showList: false,
      isAdded: false,
      selectedOption: "options",
      options: [
        {
          id: "01",
          label: "Sort top to bottom",
          value: "sort top to bottom",
          type: "radio",
        },
        {
          id: "02",
          label: "Sort left to right",
          value: "sort left to right",
          type: "radio",
        },
        {
          id: "03",
          type: "hr",
        },
        {
          label: "Case sensitive",
          value: "case sensitive",
          type: "checkbox",
        },
      ],
      previousScope: null,
    };
  },
  computed: {
    dragBoxHeadStyle() {
      return { marginBottom: "12px" };
    },
    dragBoxStyle() {
      return { backgroundColor: "#fdfeff" };
    },
  },
  mounted() {
    setTimeout(() => {
      this.bindScope();
    }, 200);
  },
  beforeUnmount() {
    this.$store.dispatch("setScope", this.previousScope);
    hotkeys.unbind("*,escape", HotkeyScopes.CustomSort);
  },
  methods: {
    closeDropdown(option, key) {
      option[key] = false;
      this.currentIndex = null;
    },
    bindScope() {
      this.previousScope = hotkeys.getScope();
      this.$store.dispatch("setScope", HotkeyScopes.CustomSort);

      hotkeys("escape", HotkeyScopes.CustomSort, (event) => {
        event.preventDefault();
        if (this.showList) {
          this.showList = false;
        } else {
          this.closeCustomSort();
        }
      });

      hotkeys("*", HotkeyScopes.CustomSort, (event, handler) => {
        event.preventDefault();
        const isNumKey = event.code.startsWith("Numpad") || event.code.startsWith("Digit");
        if (!isNumKey) return;
        if (!this.showList) return;

        if (this.keyPressed === null) return (this.keyPressed = event.key);
        const shortCut = this.keyPressed + event.key;
        this.handleSelection(shortCut);
        // reset values.
        this.keyPressed = null;
      });
    },
    handleOutsideClick() {
      if (this.showList) {
        this.showList = false;
      }
    },
    openDropdown(option, $event, dropdownKey, positionKey) {
      const selectedDiv = $event.currentTarget.parentElement;
      // const selectedDiv = document.querySelector(".selected");
      const selectedRect = selectedDiv?.getBoundingClientRect();

      option[positionKey] = {
        top: `${selectedRect.top}px`,
        left: `${selectedRect.left}px`,
        width: `${selectedDiv.offsetWidth}px`,
        position: "fixed",
        minWidth: "max-content",
        zIndex: 9999, // High z-index to float above all
      };

      this.sortOptions.forEach((opt) => {
        opt.columnOpen = false;
        opt.sortByOpen = false;
        opt.orderByOpen = false;
      });
      // Close others if needed here (optional)
      option[dropdownKey] = !option[dropdownKey];
    },
    addSortOption() {
      this.sortOptions.push({
        columnValue: "Sort by",
        columnOpen: false,
        sortby: "Values",
        sortByOpen: false,
        orderBy: "A to Z",
        orderByOpen: false,
      });
    },
    removeSortOption() {
      // Prevent removing the last remaining item
      if (this.sortOptions.length > 1) {
        this.sortOptions.pop();
      }
    },
    closeCustomSort(event) {
      event?.preventDefault();
      this.$store.dispatch("pdesign/toggleCustomSort");
    },
    handleSelection(id) {
      const option = this.options.find((item) => item.id == id);
      if (!option) return;
      this.currentItemID = id;
      this.selectedOption = option.value;
      this.showList = false;
    },
    toggleList(event) {
      event.preventDefault();
      this.showList = !this.showList;
    },
    handleAdd() {
      this.isAdded = !this.isAdded;
    },
    toggleSensitive() {
      this.isSensitive = !this.isSensitive;
    },
  },
};
</script>

<style lang="less" scoped>
@import "../../assets/styles/variables.less";

.dragResize__header i {
  cursor: pointer;
}

.selected {
  display: flex;
  gap: 0.5rem;
  padding: 4px;
  cursor: auto;
  span {
    flex: 1;
  }
  i {
    border-radius: 3px;
    font-size: 1.4rem;
    cursor: pointer;
  }
  i:not(.selectedOption) {
    background: #f5f5f5;
    color: @kk-color-primary-blue;
  }
}

.custom-sort {
  &__wrapper {
    display: flex;
    flex-direction: column;
    gap: 12px;
    height: 100%;
  }

  &__header {
    display: flex;
    justify-content: space-between;
    i {
      cursor: pointer;
    }
  }

  &__body {
    position: relative;
    height: 10rem;
    flex-grow: 1;
    overflow-y: scroll;
    padding-left: 0.8rem;
    color: @kk-color-royal-blue;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    border: 1px solid #eaedf1;
    border-radius: 4px;
    padding-top: 10px;

    &--column {
      display: grid;
      grid-template-columns: 1.5fr 1fr 1fr;
      padding: 0 9px;
    }

    .columnText {
      justify-self: center;
      max-width: auto;
    }

    .columnItem {
      position: relative;
      padding-left: 4px;
    }

    .columnItem::before {
      content: "";
      position: absolute;
      width: 1px;
      height: 80%;
      background-color: #eaedf1;
      left: -5px;
      height: 70%;
      margin: auto;
      top: 0;
      bottom: 0;
    }

    &--item {
      display: grid;
      grid-template-columns: 1.5fr 1fr 1fr;
      border-radius: 2px;

      &--active {
        background-color: @kk-sky-blue;
      }
    }

    &--sub {
      .custom-select .selected {
        padding: 0;
      }
    }
  }

  &__actions {
    display: flex;
    justify-content: right;
    gap: 1rem;

    button {
      width: 130px;
      text-align: center;
      border-radius: 4px;
      border: 1px solid rgba(4, 63, 143, 0.4);
    }

    &--cancel {
      color: rgba(8, 50, 119, 0.5);
    }

    &--add {
      background: #043f8f;
      color: @kk-color-white;
    }
  }
}

.options {
  display: flex;
  align-items: center;
  gap: 1rem;

  &--listWrapper,
  &__addMinus {
    border: 1px solid #eaedf1;
    border-radius: 4px;
  }

  &--listWrapper {
    position: relative;
    width: max-content;
  }

  &__addMinus {
    padding: 8px 12px;
    display: flex;
    gap: 1.2rem;
    align-items: center;
    i {
      cursor: pointer;
    }
  }

  &--list {
    position: absolute;
    left: 0;
    right: 0;
    top: 0px;
    z-index: 1;
    color: @kk-color-primary-blue;
    overflow: hidden;
    border-radius: 4px;
    background-color: @kk-color-white;
    border: 1px solid #eaedf1;
    width: max-content;
    min-width: 100%;
    padding: 4px;

    label {
      gap: 0.5rem;
      cursor: pointer;
    }
  }

  &--headItem {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    cursor: pointer;

    i {
      font-size: 2rem;
    }
  }

  &--item {
    margin-bottom: 2px;

    label {
      width: 100%;
      padding: 5px 10px;
      border-radius: 2px;
      &:hover {
        background-color: @kk-sky-blue;
      }
    }

    &__seperator {
      width: 90%;
      margin: 0 auto;
      height: 0.75px;
      background: #eaedf1;
      margin-top: 10px;
      margin-bottom: 5px;
    }

    &.active {
      background-color: @kk-color-primary-blue;
      color: @kk-color-light-white;
    }
  }

  &--checkbox {
    margin-left: auto;
    label {
      gap: 0.5rem;
    }
  }
}
</style>
